import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useAppDispatch } from '../../../../store/store';
import { RootState } from '../../../../reducers';
import {
    fetchTeamSummary,
    triggerTeamSummaryGeneration,
    stopTeamSummaryPolling,
    resetState,
} from '../../../../store/teamAiSummary.slice';
import {
    Box,
    Paper,
    Typography,
    Stack,
    CircularProgress,
    IconButton,
    Tooltip,
    Link,
    Divider,
    Collapse,
    Alert,
    Chip,
    Button,
    TextField,
    InputAdornment,
    Pagination,
    Select,
    MenuItem,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { apiString as API_URL } from '../../../../utils/constants';

interface TeamAiSummaryProps {
    teamId: string;
}

const TeamAiSummary: React.FC<TeamAiSummaryProps> = ({ teamId }) => {
    const dispatch = useAppDispatch();
    const { summary, progress, isLoading, isGenerating, error } = useSelector(
        (state: RootState) => state.teamAiSummary
    );

    const [isAiEnabled, setIsAiEnabled] = useState<boolean>(false);
    const [checkingStatus, setCheckingStatus] = useState<boolean>(true);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        const cached = localStorage.getItem(`team_ai_summary_collapsed_${teamId}`);
        return cached ? cached === 'true' : false;
    });

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(5);
    const [collapsedTiles, setCollapsedTiles] = useState<Record<string, boolean>>({});

    const memberTiles = summary?.memberTiles ?? [];

    const filteredTiles = useMemo(() => {
        if (!searchTerm.trim()) return memberTiles;
        const term = searchTerm.toLowerCase().trim();
        return memberTiles.filter((t) => {
            const fullName = `${t.firstName || ''} ${t.lastName || ''}`.toLowerCase();
            const memberId = (t.memberId || '').toLowerCase();
            return fullName.includes(term) || memberId.includes(term);
        });
    }, [memberTiles, searchTerm]);

    const totalPages = Math.max(1, Math.ceil(filteredTiles.length / pageSize));
    const safePage = Math.min(Math.max(1, page), totalPages);

    const paginatedTiles = useMemo(() => {
        const startIndex = (safePage - 1) * pageSize;
        return filteredTiles.slice(startIndex, startIndex + pageSize);
    }, [filteredTiles, safePage, pageSize]);

    const areAllCollapsed = useMemo(() => {
        if (filteredTiles.length === 0) return false;
        return filteredTiles.every((t) => !!collapsedTiles[t.memberId]);
    }, [filteredTiles, collapsedTiles]);

    useEffect(() => {
        const cached = localStorage.getItem(`team_ai_summary_collapsed_${teamId}`);
        setIsCollapsed(cached ? cached === 'true' : false);
    }, [teamId]);

    useEffect(() => {
        const checkStatus = async () => {
            try {
                const res = await axios.get<boolean>(`${API_URL}/chpai/team-summary/enabled`);
                setIsAiEnabled(res.data);
            } catch (e) {
                console.error('Failed to check AI status:', e);
                setIsAiEnabled(false);
            } finally {
                setCheckingStatus(false);
            }
        };
        checkStatus();
    }, []);

    useEffect(() => {
        if (!teamId || !isAiEnabled) return;

        dispatch(fetchTeamSummary(teamId));

        return () => {
            dispatch(stopTeamSummaryPolling());
            dispatch(resetState());
        };
    }, [teamId, isAiEnabled, dispatch]);

    const handleRegenerate = () => {
        if (!teamId || isGenerating) return;
        dispatch(triggerTeamSummaryGeneration(teamId, true));
    };

    const toggleCollapse = () => {
        setIsCollapsed((prev) => {
            const next = !prev;
            localStorage.setItem(`team_ai_summary_collapsed_${teamId}`, String(next));
            return next;
        });
    };

    const toggleTileCollapse = (memberId: string) => {
        setCollapsedTiles((prev) => ({
            ...prev,
            [memberId]: !prev[memberId],
        }));
    };

    const handleToggleAll = () => {
        const shouldCollapse = !areAllCollapsed;
        setCollapsedTiles((prev) => {
            const next = { ...prev };
            filteredTiles.forEach((t) => {
                next[t.memberId] = shouldCollapse;
            });
            return next;
        });
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
            });
        } catch {
            return dateString;
        }
    };

    const formatDateOnly = (dateString?: string) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString(undefined, {
                dateStyle: 'medium',
            });
        } catch {
            return dateString;
        }
    };

    const getRiskChipColor = (riskLevel?: string): 'error' | 'warning' | 'success' | 'default' => {
        const lower = (riskLevel || '').toLowerCase();
        if (lower.includes('high')) return 'error';
        if (lower.includes('medium') || lower.includes('moderate')) return 'warning';
        if (lower.includes('low')) return 'success';
        return 'default';
    };

    if (checkingStatus || !isAiEnabled) {
        return null;
    }

    return (
        <Paper
            elevation={3}
            sx={{
                mb: 3,
                borderRadius: 3,
                overflow: 'hidden',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 247, 250, 0.95))',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(0, 0, 0, 0.06)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
        >
            <Box
                onClick={toggleCollapse}
                sx={{
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.015)' },
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(45deg, #2e7d32, #4caf50)',
                            color: 'white',
                            boxShadow: '0 2px 8px rgba(46, 125, 50, 0.25)',
                        }}
                    >
                        <GroupsRoundedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="700" color="text.primary">
                            AI Team Summary
                        </Typography>
                        {summary && !isGenerating && (
                            <Typography variant="caption" color="text.secondary">
                                Last updated: {formatDate(summary.generatedAt)}
                            </Typography>
                        )}
                        {isGenerating && (
                            <Typography variant="caption" color="primary.main" fontWeight="500">
                                {progress?.stage ?? 'Generating...'}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1} onClick={(e) => e.stopPropagation()}>
                    {summary?.isStale && !isGenerating && (
                        <Tooltip title="Team summary may be outdated. Click refresh to regenerate.">
                            <Chip
                                label="Outdated"
                                size="small"
                                color="warning"
                                variant="outlined"
                                onClick={handleRegenerate}
                                sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }}
                            />
                        </Tooltip>
                    )}
                    {!isGenerating && (
                        <Tooltip title={summary ? "Re-generate Team Summary" : "Generate Team Summary"}>
                            <IconButton onClick={handleRegenerate} size="small" sx={{ color: 'text.secondary' }}>
                                <RefreshRoundedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton onClick={toggleCollapse} size="small" sx={{ color: 'text.secondary' }}>
                        {isCollapsed ? <ExpandMoreRoundedIcon /> : <ExpandLessRoundedIcon />}
                    </IconButton>
                </Stack>
            </Box>

            <Collapse in={!isCollapsed}>
                <Divider />
                <Box sx={{ p: 3 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {isGenerating ? (
                        <Stack alignItems="center" justifyContent="center" spacing={2.5} py={6}>
                            <CircularProgress size={36} thickness={4.5} />
                            <Stack alignItems="center" spacing={0.5}>
                                <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                                    Summarizing Team
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {progress?.stage ?? 'Gathering member summaries and synthesizing team insights...'}
                                </Typography>
                            </Stack>
                        </Stack>
                    ) : isLoading ? (
                        <Stack alignItems="center" py={4}>
                            <CircularProgress size={28} />
                        </Stack>
                    ) : summary ? (
                        <Stack spacing={2.5}>
                            {/* Team Overview Section */}
                            <Box>
                                <Typography variant="subtitle2" fontWeight="700" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                                    Team Clinical Overview
                                </Typography>
                                {summary.markdown ? (
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: 'rgba(255, 255, 255, 0.7)',
                                            border: '1px solid rgba(0, 0, 0, 0.05)',
                                            color: 'text.primary',
                                            lineHeight: 1.7,
                                            '& p': { m: 0, mb: 1 },
                                            '& p:last-child': { mb: 0 },
                                            '& strong': { fontWeight: 600 },
                                        }}
                                    >
                                        <ReactMarkdown>{summary.markdown}</ReactMarkdown>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No team summary available yet.
                                    </Typography>
                                )}

                                {summary.citations && summary.citations.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <details>
                                            <summary style={{ fontSize: '0.8rem', color: '#666', fontWeight: '600', cursor: 'pointer', outline: 'none' }}>
                                                Team overview sources ({summary.citations.length})
                                            </summary>
                                            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75, pl: 1 }}>
                                                {summary.citations.map((c, ci) => (
                                                    <Tooltip key={ci} title={c.excerpt ?? ''} placement="top" arrow>
                                                        <Link
                                                            href={c.url}
                                                            target="_blank"
                                                            rel="noopener"
                                                            underline="hover"
                                                            sx={{
                                                                fontSize: '0.725rem',
                                                                px: 1.25,
                                                                py: 0.5,
                                                                borderRadius: 1.5,
                                                                bgcolor: 'grey.100',
                                                                color: 'text.secondary',
                                                                border: '1px solid rgba(0,0,0,0.04)',
                                                                display: 'inline-block',
                                                                '&:hover': { bgcolor: 'grey.200' },
                                                            }}
                                                        >
                                                            {c.title}
                                                        </Link>
                                                    </Tooltip>
                                                ))}
                                            </Box>
                                        </details>
                                    </Box>
                                )}
                            </Box>

                            {/* Ranked Member Summaries Section */}
                            {memberTiles && memberTiles.length > 0 ? (
                                <Box sx={{ mt: 1 }}>
                                    <Divider sx={{ mb: 2.5 }} />

                                    {/* Section Header & Actions */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight="700" color="text.primary">
                                                    Active Member Summaries & Action Items
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Ranked by urgency, warning flags, and recency of critical care notes ({memberTiles.length} active members)
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={handleToggleAll}
                                                    startIcon={areAllCollapsed ? <ExpandMoreRoundedIcon /> : <ExpandLessRoundedIcon />}
                                                    sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.8rem', fontWeight: 600 }}
                                                >
                                                    {areAllCollapsed ? 'Expand All' : 'Collapse All'}
                                                </Button>
                                            </Stack>
                                        </Box>

                                        {/* Search & Pagination Toolbar */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: 1.5,
                                                p: 1.5,
                                                borderRadius: 2,
                                                bgcolor: 'rgba(0, 0, 0, 0.02)',
                                                border: '1px solid rgba(0, 0, 0, 0.05)',
                                            }}
                                        >
                                            <TextField
                                                size="small"
                                                placeholder="Search members by name or ID..."
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setSearchTerm(e.target.value);
                                                    setPage(1);
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: searchTerm ? (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    setSearchTerm('');
                                                                    setPage(1);
                                                                }}
                                                                edge="end"
                                                                sx={{ color: 'text.secondary', p: 0.5 }}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ) : null,
                                                }}
                                                sx={{
                                                    width: { xs: '100%', sm: 300 },
                                                    bgcolor: '#ffffff',
                                                    borderRadius: 1.5,
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 1.5,
                                                        height: 36,
                                                    },
                                                }}
                                            />

                                            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                    {filteredTiles.length === 0
                                                        ? 'No matching members'
                                                        : `Showing ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filteredTiles.length)} of ${filteredTiles.length} members`}
                                                    {searchTerm && ` (filtered from ${memberTiles.length})`}
                                                </Typography>

                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Per page:
                                                    </Typography>
                                                    <Select
                                                        value={pageSize}
                                                        onChange={(e) => {
                                                            setPageSize(Number(e.target.value));
                                                            setPage(1);
                                                        }}
                                                        size="small"
                                                        sx={{
                                                            fontSize: '0.75rem',
                                                            height: 32,
                                                            bgcolor: '#ffffff',
                                                            borderRadius: 1.5,
                                                            '& .MuiSelect-select': { py: 0.5, px: 1.25 },
                                                        }}
                                                    >
                                                        <MenuItem value={5}>5</MenuItem>
                                                        <MenuItem value={10}>10</MenuItem>
                                                        <MenuItem value={25}>25</MenuItem>
                                                    </Select>
                                                </Stack>
                                            </Stack>
                                        </Box>
                                    </Box>

                                    {/* Member Tiles List or Empty Results */}
                                    {filteredTiles.length === 0 ? (
                                        <Box
                                            sx={{
                                                py: 4,
                                                px: 2,
                                                textAlign: 'center',
                                                bgcolor: 'rgba(0, 0, 0, 0.015)',
                                                borderRadius: 2,
                                                border: '1px dashed rgba(0, 0, 0, 0.12)',
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                                No member summaries found matching "{searchTerm}".
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setSearchTerm('');
                                                    setPage(1);
                                                }}
                                                sx={{ textTransform: 'none', borderRadius: 2 }}
                                            >
                                                Clear Search Filter
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Stack spacing={2}>
                                            {paginatedTiles.map((tile) => {
                                                const isTileCollapsed = !!collapsedTiles[tile.memberId];
                                                return (
                                                    <Paper
                                                        key={tile.memberId}
                                                        elevation={0}
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: 2.5,
                                                            border: '1px solid',
                                                            borderColor: tile.rank <= 3 && tile.urgencyScore >= 50 ? 'rgba(211, 47, 47, 0.28)' : 'rgba(0, 0, 0, 0.08)',
                                                            bgcolor: tile.rank <= 3 && tile.urgencyScore >= 50 ? 'rgba(253, 237, 237, 0.35)' : '#ffffff',
                                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
                                                            transition: 'all 0.2s ease-in-out',
                                                            '&:hover': {
                                                                boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
                                                                borderColor: tile.rank <= 3 && tile.urgencyScore >= 50 ? 'rgba(211, 47, 47, 0.45)' : 'rgba(0, 0, 0, 0.16)',
                                                            },
                                                        }}
                                                    >
                                                        {/* Header Row - Clickable to toggle collapse */}
                                                        <Box
                                                            onClick={() => toggleTileCollapse(tile.memberId)}
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                flexWrap: 'wrap',
                                                                gap: 1,
                                                                cursor: 'pointer',
                                                                userSelect: 'none',
                                                                mb: isTileCollapsed ? 0 : 1.5,
                                                            }}
                                                        >
                                                            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" sx={{ flexGrow: 1 }}>
                                                                <Box
                                                                    sx={{
                                                                        px: 1.2,
                                                                        py: 0.35,
                                                                        borderRadius: 1.5,
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: 700,
                                                                        bgcolor: tile.rank <= 3 ? (tile.urgencyScore >= 50 ? '#d32f2f' : '#ed6c02') : '#e0e0e0',
                                                                        color: tile.rank <= 3 ? '#ffffff' : '#424242',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                >
                                                                    #{tile.rank} Priority
                                                                </Box>
                                                                <Tooltip title="Open Member Chart in new tab">
                                                                    <Link
                                                                        component={RouterLink}
                                                                        to={`/provider/members-list/member-chart/${tile.memberId}/member-profile`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        underline="hover"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        sx={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: 0.5,
                                                                            fontWeight: 700,
                                                                            fontSize: '1.05rem',
                                                                            color: 'primary.main',
                                                                            cursor: 'pointer',
                                                                        }}
                                                                    >
                                                                        {tile.firstName} {tile.lastName}
                                                                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                                                                    </Link>
                                                                </Tooltip>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {[
                                                                        tile.age ? `Age ${tile.age}` : null,
                                                                        tile.gender,
                                                                        tile.status,
                                                                        tile.healthPlanName,
                                                                    ].filter(Boolean).join(' • ')}
                                                                </Typography>
                                                            </Stack>

                                                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                                {tile.riskLevel && (
                                                                    <Chip
                                                                        label={`${tile.riskLevel} Risk`}
                                                                        size="small"
                                                                        color={getRiskChipColor(tile.riskLevel)}
                                                                        variant="outlined"
                                                                        sx={{ fontWeight: 600, fontSize: '0.725rem' }}
                                                                    />
                                                                )}
                                                                {tile.mostRecentNoteDate && (
                                                                    <Chip
                                                                        icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                                                                        label={`Latest Note: ${formatDateOnly(tile.mostRecentNoteDate)}`}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: 'rgba(0,0,0,0.04)',
                                                                            color: 'text.secondary',
                                                                            fontSize: '0.725rem',
                                                                            fontWeight: 500,
                                                                        }}
                                                                    />
                                                                )}
                                                                <Tooltip title={isTileCollapsed ? 'Expand member summary' : 'Collapse member summary'}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleTileCollapse(tile.memberId);
                                                                        }}
                                                                        sx={{ color: 'text.secondary' }}
                                                                    >
                                                                        {isTileCollapsed ? <ExpandMoreRoundedIcon /> : <ExpandLessRoundedIcon />}
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Stack>
                                                        </Box>

                                                        {/* Collapsible Content */}
                                                        <Collapse in={!isTileCollapsed}>
                                                            {/* Identified Concerns Chips */}
                                                            {tile.identifiedConcerns && tile.identifiedConcerns.length > 0 && (
                                                                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 1.5, mt: 0.5 }}>
                                                                    {tile.identifiedConcerns.map((concern, idx) => {
                                                                        const isWarning = concern.toLowerCase().includes('warning') || concern.toLowerCase().includes('critical') || concern.toLowerCase().includes('urgent');
                                                                        return (
                                                                            <Chip
                                                                                key={idx}
                                                                                icon={isWarning ? <WarningAmberIcon sx={{ fontSize: '14px !important' }} /> : undefined}
                                                                                label={concern}
                                                                                size="small"
                                                                                color={isWarning ? 'error' : 'default'}
                                                                                variant={isWarning ? 'filled' : 'outlined'}
                                                                                sx={{
                                                                                    fontSize: '0.7rem',
                                                                                    height: 22,
                                                                                    fontWeight: isWarning ? 600 : 500,
                                                                                    ...(isWarning ? { bgcolor: '#ffebee', color: '#c62828', borderColor: '#ef9a9a' } : {}),
                                                                                }}
                                                                            />
                                                                        );
                                                                    })}
                                                                </Stack>
                                                            )}

                                                            {/* Member Summary Narrative */}
                                                            <Box
                                                                sx={{
                                                                    p: 1.75,
                                                                    borderRadius: 2,
                                                                    bgcolor: 'rgba(0, 0, 0, 0.015)',
                                                                    border: '1px solid rgba(0, 0, 0, 0.05)',
                                                                    color: 'text.primary',
                                                                    fontSize: '0.875rem',
                                                                    lineHeight: 1.65,
                                                                    '& p': { m: 0, mb: 0.75 },
                                                                    '& p:last-child': { mb: 0 },
                                                                    '& strong': { fontWeight: 600, color: 'text.primary' },
                                                                    '& ul': { my: 0.5, pl: 2.5 },
                                                                    '& li': { mb: 0.25 },
                                                                }}
                                                            >
                                                                <ReactMarkdown>{tile.summaryText}</ReactMarkdown>
                                                            </Box>

                                                            {/* Member Citations dropdown */}
                                                            {tile.citations && tile.citations.length > 0 && (
                                                                <Box sx={{ mt: 1.25 }}>
                                                                    <details>
                                                                        <summary style={{ fontSize: '0.75rem', color: '#757575', fontWeight: 600, cursor: 'pointer', outline: 'none' }}>
                                                                            Member sources ({tile.citations.length})
                                                                        </summary>
                                                                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75, pl: 0.5 }}>
                                                                            {tile.citations.map((c, ci) => (
                                                                                <Tooltip key={ci} title={c.excerpt ?? ''} placement="top" arrow>
                                                                                    <Link
                                                                                        href={c.url}
                                                                                        target="_blank"
                                                                                        rel="noopener"
                                                                                        underline="hover"
                                                                                        sx={{
                                                                                            fontSize: '0.7rem',
                                                                                            px: 1,
                                                                                            py: 0.4,
                                                                                            borderRadius: 1,
                                                                                            bgcolor: 'grey.100',
                                                                                            color: 'text.secondary',
                                                                                            border: '1px solid rgba(0,0,0,0.04)',
                                                                                            display: 'inline-block',
                                                                                            '&:hover': { bgcolor: 'grey.200' },
                                                                                        }}
                                                                                    >
                                                                                        {c.title}
                                                                                    </Link>
                                                                                </Tooltip>
                                                                            ))}
                                                                        </Box>
                                                                    </details>
                                                                </Box>
                                                            )}
                                                        </Collapse>
                                                    </Paper>
                                                );
                                            })}
                                        </Stack>
                                    )}

                                    {/* Pagination Controls */}
                                    {totalPages > 1 && (
                                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <Pagination
                                                count={totalPages}
                                                page={safePage}
                                                onChange={(_, newPage) => setPage(newPage)}
                                                color="primary"
                                                shape="rounded"
                                                size="medium"
                                                showFirstButton
                                                showLastButton
                                            />
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ mt: 2, p: 2.5, borderRadius: 2.5, border: '1px dashed rgba(0, 0, 0, 0.15)', bgcolor: 'rgba(0, 0, 0, 0.015)', textAlign: 'center' }}>
                                    <Typography variant="subtitle2" fontWeight="600" color="text.secondary">
                                        Member Summary Tiles Not Yet Compiled
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                                        This team summary was generated prior to the member summary tiles update or the tiles are currently being compiled.
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleRegenerate}
                                        startIcon={<RefreshRoundedIcon />}
                                        sx={{ textTransform: 'none', borderRadius: 2 }}
                                    >
                                        Compile Member Summaries
                                    </Button>
                                </Box>
                            )}
                        </Stack>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 1.5 }}>
                                No AI summary has been generated for this team yet.
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleRegenerate}
                                startIcon={<RefreshRoundedIcon />}
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                                Generate Team Summary
                            </Button>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default TeamAiSummary;

